import { VehicleDetailsExteriorGrading } from 'Components/pages/VehicleDetailsRedesign/sections/ConditionAndDamageSection/Components/VehicleDetailsExteriorGrading/VehicleDetailsExteriorGrading';
import type { SpecSectionField } from 'Components/SpecSection/SpecSection.types';
import type { GradingValues, NumericGradeValues } from 'Pages/vehicles/helpers/vehicle.schema';
import { GRADE_VALUES, NUMERIC_GRADE_VALUES } from 'Pages/vehicles/helpers/vehicle.schema';

import { DamageWithImage } from './Components/DamageWithImage/DamageWithImage';
import { VehicleDetailsExteriorNumericGrading } from './Components/VehicleDetailsExteriorGrading/VehicleDetailsExteriorNumericGrading';
import type { ConditionAndDamageFieldValues } from './ConditionAndDamageSection.types';

const shouldShowGradeField = (
	value: ConditionAndDamageFieldValues,
	fieldType: 'numericGrade' | 'legacyGrade',
): boolean => {
	const hasNumericGrade = Boolean(value.numericGrade);
	const hasLegacyGrade = Boolean(value.grade) && value.grade !== '-';

	if (fieldType === 'numericGrade') {
		// Show new field if numeric grade exists or it doesn't have an old grade to fallback to
		return hasNumericGrade || !hasLegacyGrade;
	}

	// Show old field if numeric grade does not exist and bsg grade exists
	return !hasNumericGrade && hasLegacyGrade;
};

export const conditionAndDamageFields: SpecSectionField<ConditionAndDamageFieldValues>[] = [
	{
		fieldName: 'grade',
		isVisible: ({ value }) => shouldShowGradeField(value, 'legacyGrade'),
		label: () => 'Exterior grade',
		value: ({ grade, make, model }) =>
			GRADE_VALUES.includes(grade as GradingValues) ? (
				<VehicleDetailsExteriorGrading
					exteriorGrade={grade as GradingValues}
					vehicleSpec={{
						make,
						model,
					}}
				/>
			) : (
				'-'
			),
	},
	{
		fieldName: 'numericGrade',
		isVisible: ({ value }) => shouldShowGradeField(value, 'numericGrade'),
		label: () => 'Exterior grade',
		value: ({ numericGrade }) =>
			NUMERIC_GRADE_VALUES.includes(numericGrade as NumericGradeValues) ? (
				<VehicleDetailsExteriorNumericGrading numericGrade={numericGrade as NumericGradeValues} />
			) : (
				'-'
			),
	},
	{
		fieldName: 'hasScratches',
		isVisible: () => true,
		label: () => 'Scratches and scuffs',
		value: ({ hasScratches, scratchesDesc }) => (
			<DamageWithImage damageDescription={scratchesDesc} hasDamage={hasScratches} kind="damage_scratches" />
		),
	},
	{
		fieldName: 'hasDents',
		isVisible: () => true,
		label: () => 'Dents',
		value: ({ dentsDesc, hasDents }) => (
			<DamageWithImage damageDescription={dentsDesc} hasDamage={hasDents} kind="damage_dents" />
		),
	},
	{
		fieldName: 'hasPaintProblems',
		isVisible: () => true,
		label: () => 'Paintwork problems',
		value: ({ hasPaintProblems, paintProblemsDesc }) => (
			<DamageWithImage damageDescription={paintProblemsDesc} hasDamage={hasPaintProblems} kind="damage_paintwork" />
		),
	},
	{
		fieldName: 'hasWindscreenProblems',
		isVisible: () => true,
		label: () => 'Windscreen damage',
		value: ({ hasWindscreenProblems, windscreenProblemsDesc }) => (
			<DamageWithImage
				damageDescription={windscreenProblemsDesc}
				hasDamage={hasWindscreenProblems}
				kind="damage_windscreen"
			/>
		),
	},
	{
		fieldName: 'hasMissingParts',
		isVisible: () => true,
		label: () => 'Broken / missing lights, mirrors, trim or fittings',
		value: ({ hasMissingParts, missingPartsDesc }) => (
			<DamageWithImage damageDescription={missingPartsDesc} hasDamage={hasMissingParts} kind="damage_missing_trims" />
		),
	},
	{
		fieldName: 'hasWarningLights',
		isVisible: () => true,
		label: () => 'Warning lights on dashboard',
		value: ({ hasWarningLights, warningLightsDesc }) => (
			<DamageWithImage
				damageDescription={warningLightsDesc}
				hasDamage={hasWarningLights}
				kind="damage_warning_lights"
			/>
		),
	},
	{
		fieldName: 'hasBeenSmokedIn',
		isVisible: () => true,
		label: () => 'Smoking in vehicle',
		value: ({ hasBeenSmokedIn }) => hasBeenSmokedIn,
	},
	{
		fieldName: 'additionalDetails',
		isVisible: () => true,
		label: () => 'Additional details',
		value: ({ additionalDetails }) => additionalDetails ?? '-',
	},
];

const MOBILIZE_CONDITION_FIELD_NAMES = new Set(['hasWarningLights', 'hasBeenSmokedIn', 'additionalDetails']);

export const mobilizeConditionFields = conditionAndDamageFields.filter((field) =>
	MOBILIZE_CONDITION_FIELD_NAMES.has(field.fieldName),
);
